import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { postLoginDestination } from '../utils/postLoginRedirect';

/**
 * For the /login and /register pages: once AuthContext has restored (and
 * validated) the stored session, a signed-in user who lands here (e.g. "Log
 * in" / "Get started" on the landing site) skips the form.
 *
 * Only the state at load counts. A sign-in made with the page's own form
 * navigates itself, so call the returned `skip()` before submitting: if the
 * form was sent while the session was still restoring, this check must not
 * navigate again once loading ends. postLoginDestination is used up by then
 * (a pending AI-app consent return would be lost and the second redirect
 * would land on the dashboard).
 */
export function useRedirectIfSignedIn(): () => void {
    const navigate = useNavigate();
    const authContext = useAuth();
    const isLoading = authContext ? authContext.isLoading : true;
    const checked = useRef(false);

    useEffect(() => {
        if (isLoading || checked.current) return;
        checked.current = true;
        if (authContext?.user) {
            navigate(postLoginDestination(authContext.userSettings?.onboarding_complete === false), { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading]);

    return () => { checked.current = true; };
}
